import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const BlogPrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-20 bg-[#F8F9FA]">
      <SEO 
        title="Blog Privacy Policy"
        description="Privacy Policy for Navaris Digital blog and content sections."
        canonicalUrl="/blog/privacy-policy"
      />
      
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-[#320547] via-[#4a1a6b] to-[#95228A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-6">
              <Shield size={32} className="text-white" />
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-4">
              Blog Privacy Policy
            </h1>
            <p className="text-white/70 text-lg">
              How we collect, use, and protect your information
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Button
            asChild
            variant="ghost"
            className="mb-8 text-gray-500 hover:text-[#320547] -ml-4"
          >
            <Link to="/blog">
              <ArrowLeft className="mr-2" size={18} /> Back to Blog
            </Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-100">
              <p className="text-gray-600 leading-relaxed text-lg mb-8">
                Navaris Digital ("Navaris", "we", "our", or "us") operates the blog and content sections of{' '}
                <a href="https://www.navarisdigital.com" className="text-[#95228A] hover:text-[#320547]">
                  https://www.navarisdigital.com
                </a>{' '}
                to share insights, articles, opinions, and informational content related to technology, digital engineering, and industry trends. This Privacy Policy explains how information is collected, used, and protected when users access, read, comment on, or subscribe to our blog content.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                Information We Collect
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                When you visit our blog pages, we may automatically collect non-personal information such as IP address, browser type, device information, pages viewed, time spent on articles, and referral sources through cookies and analytics tools to understand readership behavior, improve content quality, and optimize website performance.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                If you voluntarily subscribe to our blog, comment on posts, download content, or contact us through blog-related forms, we may collect personal information such as your name, email address, and any information you choose to share in comments or messages.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                How We Use Your Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                This information is used solely to manage subscriptions, publish or moderate comments, respond to inquiries, send blog updates or newsletters (if opted in), and improve our content offerings.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                Information Sharing
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Navaris Digital does not sell, rent, or trade personal information collected through its blog pages; however, limited data may be shared with trusted third-party service providers such as analytics, email delivery, or content management platforms strictly for operational purposes and in accordance with confidentiality obligations.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                Data Security
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                We implement reasonable technical and organizational measures to safeguard blog-related data, though no internet-based system can be guaranteed to be completely secure.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                Data Retention
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Personal information is retained only as long as necessary to fulfill the purposes described or to comply with legal requirements, and users may request access, correction, or deletion of their personal data by contacting us.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                External Links
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Blog pages may contain external links to third-party websites or references, and Navaris Digital is not responsible for the privacy practices or content of those external sites.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                Children's Privacy
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our blog content is intended for a general audience and is not directed toward children under the age of 13.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                Policy Updates
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                This Privacy Policy may be updated periodically, and any changes will be reflected on this page.
              </p>

              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#320547] mt-8 mb-4">
                Contact Us
              </h2>
              <p className="text-gray-600 leading-relaxed">
                For questions related to blog privacy or data usage, please contact us at{' '}
                <a 
                  href="mailto:info@navarisdigital.com" 
                  className="text-[#95228A] hover:text-[#320547] inline-flex items-center gap-1"
                >
                  <Mail size={16} />
                  info@navarisdigital.com
                </a>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPrivacyPolicy;
